"use client";

import { type ReactNode } from "react";
import { FaPrescriptionBottle, FaPrint, FaStore } from "react-icons/fa";
import { FiAlertTriangle, FiImage } from "react-icons/fi";

import { usePathname, useSearchParams } from "next/navigation";

import { DoseDilutionLabel } from "@/components/dose/dilution-label";
import { DoseDropdown } from "@/components/dose/dose-dropdown";
import { MedicationNameTooltip } from "@/components/dose/medication-name-tooltip";
import NeedsSignOff from "@/components/dose/needs-sign-off";
import { ProgressBarLink } from "@/components/progress-bar";
import SignedOff from "@/components/signed-off";
import { Badge, Tooltip } from "@/components/ui";
import IdDisplay from "@/components/ui/id-display";
import {
  TableCell,
  TableHead,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from "@/components/ui/table";

import { useUser } from "@/contexts/user-provider";

import {
  type MedicationDayTotal,
  formatDoseComponentAmount,
  formatDoseCount,
  formatMedicationTotalAmount,
  getDoseComponentName,
  getDoseComponents,
} from "@/lib/dose-components";
import { formatDoseLocations } from "@/lib/dose-locations";
import {
  formatDate,
  formatPhoneNumber,
  formatQuantity,
  formatStaffName,
  formatUnit,
  toClinicDateKey,
} from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";

import type { MedicationStatus, MedicationSummary } from "@/types/medication";
import type { NoteDetail, NoteListItem } from "@/types/notes";
import type { PatientDetail } from "@/types/patient";

export const DETAILED_DOSE_TABLE_COLUMN_COUNT = 6;

export function getDoseTableLabel(medication: MedicationSummary): string {
  return getDoseComponents(medication)
    .map((component) => `${getDoseComponentName(component)}`)
    .join(" + ");
}

export function getDoseTableDoseAmountSummary(medication: MedicationSummary) {
  const components = getDoseComponents(medication);
  if (components.length === 1) {
    const amount = formatDoseComponentAmount(components[0]);
    return (
      <div
        className="line-clamp-2 whitespace-normal break-words"
        title={amount}
      >
        {amount}
      </div>
    );
  }
  return (
    <>
      {components.map((component) => {
        const label = `${formatDoseComponentAmount(component)} - ${getDoseComponentName(component)}`;
        return (
          <div
            key={component.id}
            className="line-clamp-2 whitespace-normal break-words"
            title={label}
          >
            {label}
          </div>
        );
      })}
    </>
  );
}

export function DoseStatusBadge({
  status,
}: {
  status?: MedicationStatus | null;
}) {
  if (status !== "inactive" && status !== "deleted") return null;
  const isDeleted = status === "deleted";

  return (
    <span
      className={cx(
        "flex items-center rounded px-1.5 py-0.5 text-tiny font-medium",
        isDeleted
          ? "bg-status-error-bg text-status-error-text"
          : "bg-status-warning-bg text-status-warning-text",
      )}
    >
      {isDeleted ? "Deleted" : "Inactive"}
    </span>
  );
}

export function DetailedDoseTableHead() {
  return (
    <TableHead>
      <TableHeaderRow>
        <TableHeaderCell className={"w-1/4"}>Medication</TableHeaderCell>
        <TableHeaderCell className="w-2/12">Dose Amount</TableHeaderCell>
        <TableHeaderCell className="w-3/12">Provider</TableHeaderCell>
        <TableHeaderCell className="w-2/12">Location</TableHeaderCell>
        <TableHeaderCell className="w-24">Date</TableHeaderCell>
        <TableHeaderCell className="w-12"></TableHeaderCell>
      </TableHeaderRow>
    </TableHead>
  );
}

/** Heads one medication's doses for a day, with their total. */
function DetailedDoseSummaryRow({ total }: { total: MedicationDayTotal }) {
  return (
    <TableRow selectable={false} className="bg-background-secondary">
      <TableCell className="whitespace-normal">
        <span className="flex items-center gap-2">
          <span className="text-body-strong text-foreground">{total.name}</span>
          <Badge>{formatDoseCount(total.doseCount)}</Badge>
        </span>
      </TableCell>
      <TableCell className="whitespace-normal text-body-strong tabular-nums text-foreground">
        {formatMedicationTotalAmount(total)}
      </TableCell>
      <TableCell colSpan={DETAILED_DOSE_TABLE_COLUMN_COUNT - 2} />
    </TableRow>
  );
}

/** One line naming the pharmacy an e-prescription went to; address and phone on hover. */
function DosePharmacy({
  name,
  address,
  phone,
}: {
  name: string;
  address?: string | null;
  phone?: string | null;
}) {
  const formattedPhone = formatPhoneNumber(phone ?? undefined);
  // The name is truncated to one line, so the tooltip always carries the full name too.
  return (
    <Tooltip
      triggerAsChild
      content={
        <div
          className="flex flex-col gap-0.5 select-text"
          data-testid="dose-pharmacy-details"
        >
          <span className="font-medium">{name}</span>
          {address && <span>{address}</span>}
          {formattedPhone && <span>{formattedPhone}</span>}
        </div>
      }
    >
      <span
        className="flex w-full min-w-0 items-center gap-1 text-caption text-foreground-secondary"
        data-testid="dose-pharmacy"
        tabIndex={0}
      >
        <FaStore className="size-2.5 shrink-0" aria-hidden="true" />
        <span className="truncate">Sent to {name}</span>
      </span>
    </Tooltip>
  );
}

function FaceChartLink({ attachmentId }: { attachmentId: string }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const returnTo = `${pathname}${search ? `?${search}` : ""}`;

  return (
    <ProgressBarLink
      href={`/attachments/${attachmentId}/annotate?returnTo=${encodeURIComponent(returnTo)}`}
      className="inline-flex items-center gap-1 text-caption font-medium text-primary hover:underline"
    >
      <FiImage className="size-3 shrink-0" />
      View face chart
    </ProgressBarLink>
  );
}

interface DetailedDoseTableRowProps {
  medication: MedicationSummary;
  patient: PatientDetail;
  selectedNote?: NoteListItem | NoteDetail | null;
  onRequestSignOff: (medication: MedicationSummary) => void;
  children?: ReactNode;
  metadata?: ReactNode;
  /** The day's total for this medication, shown in a row above this dose. */
  summary?: MedicationDayTotal;
}

export function DetailedDoseTableRow({
  medication,
  patient,
  selectedNote,
  onRequestSignOff,
  children,
  metadata,
  summary,
}: DetailedDoseTableRowProps) {
  const components = getDoseComponents(medication);
  const medicationName = getDoseTableLabel(medication);
  const createdFormatted = formatDate(medication.createdDate, true, true);
  const doseFormatted = medication.doseDate
    ? formatDate(medication.doseDate, true, true)
    : null;
  const sameDayCreatedAndAdministered =
    !!doseFormatted && createdFormatted === doseFormatted;
  const administeredDay = toClinicDateKey(
    medication.doseDate ?? medication.createdDate,
  );
  const labelPrintedFormatted = medication.labelPrintedDate
    ? formatDate(medication.labelPrintedDate, true, true)
    : null;
  // A face chart dose's comment is only the marker naming its chart; the link replaces it.
  const comment = medication.chartAttachmentId ? null : medication.comment;
  const { currentUser } = useUser();
  // The chart opens in its editor: reading it needs gallery or notes access, saving needs notes write.
  const canOpenChart =
    (hasPermission(currentUser, PERMISSIONS.CLINICAL_GALLERY_READ) ||
      hasPermission(currentUser, PERMISSIONS.CLINICAL_NOTES_READ)) &&
    hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE);

  return (
    <>
      {summary && <DetailedDoseSummaryRow total={summary} />}
      <TableRow
        rowId={medication.id}
        selectable={false}
        className={cx(comment && "!border-b-0")}
      >
        <TableCell className="overflow-visible whitespace-normal">
          <div className="flex w-full min-w-0 flex-col items-start gap-0.5">
            <div className="flex w-full min-w-0 items-start gap-2">
              <div className="flex min-w-0 flex-1 items-baseline gap-2">
                <MedicationNameTooltip name={medicationName} grow={false} />
                <DoseDilutionLabel components={components} />
              </div>
              {medication.dosespotMedicationId && (
                <span
                  className="flex shrink-0 items-center gap-0.5 rounded bg-status-info-bg px-1.5 py-0.5 text-tiny font-medium text-status-info-text"
                  title="E-prescribed"
                >
                  <FaPrescriptionBottle className="size-2.5" />
                  DoseSpot
                </span>
              )}
              <DoseStatusBadge status={medication.status} />
              {children}
            </div>
            {medication.frequency && (
              <div className="text-caption text-muted-foreground">
                {medication.frequency}
              </div>
            )}
            {medication.pharmacyName && (
              <DosePharmacy
                name={medication.pharmacyName}
                address={medication.pharmacyAddress}
                phone={medication.pharmacyPhone}
              />
            )}
            <div className="flex flex-wrap items-center gap-1">
              {components
                .flatMap((component) =>
                  (component.lots ?? []).map((lot) => ({
                    ...lot,
                    quantity: component.inventoryLots?.find(
                      (entry) => entry.shipmentId === lot.shipmentId,
                    )?.quantity,
                    unit: component.inventoryLots?.find(
                      (entry) => entry.shipmentId === lot.shipmentId,
                    )?.unit,
                  })),
                )
                .map((lot, index) => {
                  const expiry = lot.expiryDate;
                  const expiredWhenGiven = !!expiry && expiry < administeredDay;
                  return (
                    <Badge
                      key={`${lot.shipmentId}-${index}`}
                      variant={expiredWhenGiven ? "error" : "default"}
                      icon={expiredWhenGiven ? FiAlertTriangle : undefined}
                      title={
                        expiry
                          ? `${expiredWhenGiven ? "Expired before this dose was given" : "Expires"}: ${formatDate(expiry)}`
                          : undefined
                      }
                    >
                      <span>
                        {`${lot.lotNumber || "Unlabeled lot"}${
                          lot.quantity !== undefined
                            ? `: ${formatQuantity(lot.quantity)}${lot.unit ? ` ${formatUnit(lot.unit)}` : ""}`
                            : ""
                        }`}
                      </span>
                      {lot.selectionMethod === "AUTOMATIC" && (
                        <span>Automatically selected</span>
                      )}
                      {expiry && (
                        <span
                          className={cx(
                            !expiredWhenGiven && "text-foreground-secondary",
                          )}
                        >
                          {`· Exp ${formatDate(expiry, { month: "numeric", day: "numeric", year: "2-digit" })}`}
                        </span>
                      )}
                    </Badge>
                  );
                })}
              {medication.chartAttachmentId && canOpenChart && (
                <FaceChartLink attachmentId={medication.chartAttachmentId} />
              )}
              {metadata}
              <IdDisplay id={medication.id} className="text-caption" />
            </div>
          </div>
        </TableCell>
        <TableCell className="min-w-0 whitespace-normal text-body text-foreground-secondary">
          {getDoseTableDoseAmountSummary(medication)}
        </TableCell>
        <TableCell className="space-y-1">
          <div
            className={cx(
              "text-body",
              !medication.provider && "text-foreground-muted",
            )}
          >
            {medication.provider
              ? formatStaffName(medication.provider)
              : "None specified"}
          </div>
          {medication.createdBy && (
            <div className="text-caption text-foreground-secondary">
              Created by {formatStaffName(medication.createdBy)}
            </div>
          )}
          <NeedsSignOff
            medication={medication}
            onRequestSignOff={onRequestSignOff}
          />
          <SignedOff
            signOff={medication}
            showLabel={false}
            showMetadata
            className="min-w-0 max-w-44"
            iconClassName="text-status-success-text"
          />
          {medication.labelPrintedDate && (
            <Tooltip
              content={`Label printed${labelPrintedFormatted ? ` on ${labelPrintedFormatted}` : ""}`}
              triggerAsChild
              triggerOnlyHover
            >
              <span className="inline-flex min-w-0 max-w-full items-center gap-1 overflow-hidden whitespace-nowrap text-caption text-status-success-text">
                <FaPrint className="inline-block shrink-0" />
                <span className="min-w-0 truncate">
                  Label printed
                  {labelPrintedFormatted && (
                    <span className="text-foreground-secondary">
                      {" "}
                      · {labelPrintedFormatted}
                    </span>
                  )}
                </span>
              </span>
            </Tooltip>
          )}
          {medication.sideEffects && (
            <div className="break-words text-caption italic text-muted-foreground">
              <span className="font-medium not-italic">Side effects:</span>{" "}
              {medication.sideEffects}
            </div>
          )}
        </TableCell>
        <TableCell className="min-w-0 whitespace-normal text-body text-foreground-secondary">
          <div
            className="line-clamp-3 break-words"
            title={formatDoseLocations(medication) || undefined}
          >
            {formatDoseLocations(medication) || "None specified"}
          </div>
        </TableCell>
        <TableCell className="text-body text-foreground-secondary">
          {sameDayCreatedAndAdministered ? (
            <>
              <span className="block text-caption text-muted-foreground text-wrap">
                Created and administered:
              </span>
              {createdFormatted}
            </>
          ) : (
            <>
              <span className="block text-caption text-muted-foreground">
                Created:
              </span>
              {createdFormatted}
              {doseFormatted && (
                <>
                  <span className="block text-caption text-muted-foreground">
                    Administered:
                  </span>
                  {doseFormatted}
                </>
              )}
            </>
          )}
        </TableCell>
        <TableCell>
          <DoseDropdown
            patient={patient}
            selectedNote={selectedNote}
            medication={medication}
          />
        </TableCell>
      </TableRow>
      {comment && (
        <TableRow
          className="!border-t-0 text-foreground-secondary"
          selectable={false}
        >
          <TableCell
            colSpan={DETAILED_DOSE_TABLE_COLUMN_COUNT}
            className="w-full max-w-full !overflow-visible !whitespace-normal break-words border-b border-border pb-4 text-left text-body !pt-0"
          >
            <div className="break-words italic text-muted-foreground">
              {comment}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
